import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { FloppyDisk, CheckSquare, ListBullets, Phone, Warning, Lightning } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Phase 4 — Daily Work Log. Staff fill 5 free-form fields per day (one row per
// user × date). Admin can view all staff entries with a staff filter.

const FIELDS = [
  { key: 'completed_today', label: 'Completed Today',     icon: CheckSquare, placeholder: 'What you finished today…' },
  { key: 'pending_work',    label: 'Pending Work',        icon: ListBullets, placeholder: 'Still in your queue…' },
  { key: 'followups_done',  label: 'Customer Follow-ups', icon: Phone,       placeholder: 'Calls / WhatsApp / e-mails…' },
  { key: 'issues',          label: 'Issues Faced',        icon: Warning,     placeholder: 'Blockers, missing inputs, downtime…' },
  { key: 'tomorrow_plan',   label: "Tomorrow's Plan",     icon: Lightning,   placeholder: 'What you intend to tackle tomorrow…' },
];

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const EMPTY = FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {});

export default function WorkLogPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [logs, setLogs] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filterUser, setFilterUser] = useState('');
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [viewTarget, setViewTarget] = useState(null);

  const refresh = useCallback(() => {
    const params = new URLSearchParams({ month });
    if (isAdmin && filterUser) params.set('user_id', filterUser);
    api.get(`/work-log?${params.toString()}`).then(r => setLogs(r.data || [])).catch(() => setLogs([]));
  }, [month, isAdmin, filterUser]);

  useEffect(() => {
    if (isAdmin) {
      api.get('/users').then(r => setStaff(r.data.filter(u => u.active !== false))).catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => { refresh(); }, [refresh]);

  // Hydrate the form when the user navigates to a date that already has a log.
  useEffect(() => {
    if (isAdmin) return; // Admin's form-card is hidden — no need to hydrate.
    const existing = logs.find(l => l.date === form.date && l.user_id === user.id);
    setForm(prev => ({
      date: prev.date,
      ...EMPTY,
      ...(existing ? Object.fromEntries(FIELDS.map(f => [f.key, existing[f.key] || ''])) : {}),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.date, logs, isAdmin]);

  const save = async () => {
    const hasContent = FIELDS.some(f => (form[f.key] || '').trim());
    if (!hasContent) { toast.error('At least one field is required'); return; }
    setSaving(true);
    try {
      await api.post('/work-log', form);
      toast.success('Work log saved');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="work-log-page">
      <p className="text-xs text-[#52525B]">
        {isAdmin
          ? 'Review every staff member\'s daily work log. Filter by staff and month.'
          : 'A five-field journal of your day. Fill at the end of every shift — admin reviews these to track progress.'}
      </p>

      {/* Staff editor — admins skip this and review-only below. */}
      {!isAdmin && (
        <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Log Date</Label>
                <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-lg mt-1 w-44" data-testid="log-date" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FIELDS.map(f => {
                const Icon = f.icon;
                return (
                  <div key={f.key}>
                    <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B] flex items-center gap-1.5">
                      <Icon size={12} className="text-[#3B82F6]" />
                      {f.label}
                    </Label>
                    <Textarea
                      rows={3}
                      placeholder={f.placeholder}
                      value={form[f.key]}
                      onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                      className="rounded-lg mt-1 text-sm"
                      data-testid={`log-${f.key}`}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="save-log-btn">
                <FloppyDisk size={16} className="mr-1.5" /> {saving ? 'Saving…' : 'Save Log'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History */}
      <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <p className="text-sm font-semibold text-[#0A0A0A]">{isAdmin ? 'All Logs' : 'My Log History'}</p>
            <div className="flex items-center gap-2 flex-wrap">
              {isAdmin && (
                <Select value={filterUser || 'all'} onValueChange={v => setFilterUser(v === 'all' ? '' : v)}>
                  <SelectTrigger className="rounded-lg w-44 h-8 text-xs" data-testid="filter-staff"><SelectValue placeholder="All Staff" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff</SelectItem>
                    {staff.map(s => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              )}
              <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="rounded-lg w-40 h-8 text-xs" data-testid="log-month-filter" />
            </div>
          </div>

          {logs.length === 0 ? (
            <p className="text-sm text-[#52525B] text-center py-8">No logs for this period.</p>
          ) : (
            <div className="space-y-2">
              {logs.map(l => (
                <button
                  type="button"
                  key={l.id}
                  onClick={() => setViewTarget(l)}
                  className="w-full text-left border border-[#E4E4E7] rounded-xl p-3 hover:bg-[#FAFAFA] transition-colors"
                  data-testid={`log-row-${l.id}`}
                >
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-[#3B82F6]">{fmtDate(l.date)}</span>
                      {isAdmin && <span className="text-xs text-[#52525B]">· {l.user_name}</span>}
                    </div>
                    {l.updated_at && l.updated_at !== l.created_at && (
                      <span className="text-[10px] text-[#A1A1AA]">edited {new Date(l.updated_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-xs text-[#52525B]">
                    {FIELDS.map(f => (
                      <div key={f.key} className="truncate">
                        <span className="text-[#A1A1AA]">{f.label}:</span> <span className="text-[#0A0A0A]">{l[f.key] || '—'}</span>
                      </div>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!viewTarget} onOpenChange={v => { if (!v) setViewTarget(null); }}>
        <DialogContent className="rounded-xl border-[#E4E4E7] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{fmtDate(viewTarget?.date)}{isAdmin && viewTarget ? ` — ${viewTarget.user_name}` : ''}</DialogTitle>
            <DialogDescription>Daily work log</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {FIELDS.map(f => {
              const Icon = f.icon;
              return (
                <div key={f.key} className="border border-[#E4E4E7] rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B] flex items-center gap-1.5 mb-1">
                    <Icon size={12} className="text-[#3B82F6]" />
                    {f.label}
                  </p>
                  <p className="text-sm text-[#0A0A0A] whitespace-pre-wrap">{viewTarget?.[f.key] || <span className="text-[#A1A1AA]">—</span>}</p>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
